import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { v4 as uuidv4 } from 'uuid';

// v49 fix-4: маппер JSONB transaction (scoring AlertWriter) → UI shape.
// БД хранит {amount,currency,asset,scored_at,operation_type,scoring_request_id,tx_hash,from_address,to_address,marble_outcome,marble_score,reason}.
// UI ожидает {id,amount(number),currency,type,direction,status,createdAt,accountId,counterpartyName,counterpartyAccount,description}.
function mapAmlTransaction(raw: any, fallbackCreatedAt: string): any {
  if (!raw || typeof raw !== 'object') {
    return {
      id: '', amount: 0, currency: '', type: 'transfer',
      direction: 'out', status: 'pending',
      createdAt: fallbackCreatedAt, accountId: '',
    };
  }
  // operation_type: DEPOSIT_OPERATION / WITHDRAW_OPERATION / TRANSFER_OPERATION / TRADE_OPERATION
  const opType = String(raw.operation_type ?? '').toUpperCase();
  let type: 'deposit' | 'withdraw' | 'transfer' | 'trade' = 'transfer';
  let direction: 'in' | 'out' = 'out';
  if (opType.includes('DEPOSIT')) { type = 'deposit'; direction = 'in'; }
  else if (opType.includes('WITHDRAW')) { type = 'withdraw'; direction = 'out'; }
  else if (opType.includes('TRADE')) { type = 'trade'; direction = 'out'; }
  else { type = 'transfer'; direction = 'out'; }

  // createdAt: scored_at — unix seconds; иначе fallback на created_at алерта.
  let createdAt = fallbackCreatedAt;
  if (typeof raw.scored_at === 'number' && raw.scored_at > 0) {
    createdAt = new Date(raw.scored_at * 1000).toISOString();
  } else if (typeof raw.scored_at === 'string' && raw.scored_at) {
    const n = Number(raw.scored_at);
    if (Number.isFinite(n) && n > 0) createdAt = new Date(n * 1000).toISOString();
  }

  const amountNum = Number(raw.amount ?? 0) || 0;
  const currency = String(raw.currency || raw.asset || '');

  // status — derive по marble_outcome/reason: decline/block_and_review → blocked; review → pending; approve → completed.
  const outcome = String(raw.marble_outcome ?? raw.reason ?? '').toLowerCase();
  let status: 'pending' | 'blocked' | 'completed' = 'pending';
  if (outcome.includes('decline') || outcome.includes('block')) status = 'blocked';
  else if (outcome.includes('approve')) status = 'completed';

  // Адрес нашего счёта (для deposit — to_address; для withdraw — from_address). Counterparty — противоположный.
  let accountId = '';
  let counterpartyAccount = '';
  if (direction === 'in') {
    accountId = String(raw.to_address ?? '');
    counterpartyAccount = String(raw.from_address ?? '');
  } else {
    accountId = String(raw.from_address ?? '');
    counterpartyAccount = String(raw.to_address ?? '');
  }

  return {
    // id — приоритет tx_hash (стабильнее), потом scoring_request_id.
    id: String(raw.tx_hash || raw.scoring_request_id || ''),
    amount: amountNum,
    currency,
    type,
    direction,
    status,
    createdAt,
    accountId,
    counterpartyAccount: counterpartyAccount || undefined,
    counterpartyName: undefined,
    description: raw.reason ? String(raw.reason) : undefined,
    // Дополнительные поля для AML-карточки (не входят в base shape, но UI читает).
    txHash: raw.tx_hash ? String(raw.tx_hash) : undefined,
    blockchain: raw.blockchain ? String(raw.blockchain) : undefined,
    marbleScore: typeof raw.marble_score === 'number' ? raw.marble_score : undefined,
    scoringRequestId: raw.scoring_request_id ? String(raw.scoring_request_id) : undefined,
  };
}

class ApiService {
  private axiosInstance: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: any[] = [];
  private redirectingToLogin = false;

  constructor() {
    // Динамически определяем URL API при запуске
    const getApiUrl = () => {
      // Сначала проверяем глобальную переменную window (устанавливается в runtime)
      if (window.API_URL) {
        return window.API_URL;
      }
      // Затем проверяем переменную окружения (для разработки)
      if (process.env.REACT_APP_API_URL) {
        return process.env.REACT_APP_API_URL;
      }
      // Если ничего не установлено - возвращаем ошибку
      throw new Error('API URL не установлен в переменных окружения');
    };

    this.axiosInstance = axios.create({
      baseURL: getApiUrl(),
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Interceptors for requests
    this.axiosInstance.interceptors.request.use(
      config => {
        const token = localStorage.getItem('token');
        const isProfileRequest = config.url?.startsWith('/profile');

        // Add Authorization token if available
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
          if (isProfileRequest) {
            console.log(
              'Authorization token добавлен для запроса профиля:',
              token
            );
          }
        } else if (isProfileRequest) {
          console.error('Токен отсутствует в localStorage для запроса профиля');
        }

        // Add requestId to every request
        config.headers['requestId'] = uuidv4();

        // Add project context for multi-tenancy filtering
        const projectId = localStorage.getItem('currentProjectId');
        if (projectId) {
          config.headers['X-Project-ID'] = projectId;
        }

        return config;
      },
      error => Promise.reject(error)
    );

    // Interceptors for responses
    this.axiosInstance.interceptors.response.use(
      response => response,
      error => {
        const originalRequest = error.config;

        // MENU-AUTH-1: рефрешим токен ТОЛЬКО на 401 (протухший/битый токен).
        // 403 = «нет прав» — НЕ рефрешим (раньше любой permission-403 запускал
        // refresh → churn токена → «сброс» меню). admin-api теперь отдаёт 401 на
        // невалидный токен и 403 только на отсутствие права.
        //
        // Сами auth-эндпоинты (refresh/login) ИЗ refresh-логики исключены: если
        // /auth/v1/refresh сам отдал 401 (refresh-токен невалиден/JWT-secret сменился) —
        // не пытаемся рефрешить рекурсивно, а чисто разлогиниваем (handleRefreshTokenError
        // вызывается в refreshToken().catch).
        const reqUrl: string = originalRequest?.url || '';
        const isAuthEndpoint = reqUrl.includes('/auth/v1/refresh') || reqUrl.includes('/auth/v1/auth');

        if (
          error.response &&
          error.response.status === 401 &&
          !originalRequest._retry &&
          !isAuthEndpoint
        ) {
          if (!this.isRefreshing) {
            this.isRefreshing = true;
            originalRequest._retry = true;

            const refreshToken = localStorage.getItem('refreshToken');

            return this.refreshToken(refreshToken)
              .then(newToken => {
                localStorage.setItem('token', newToken);
                // Same-tab сигнал: localStorage.setItem НЕ вызывает storage-event
                // в текущей вкладке. Без этого sidebar/бейджи держат старый набор
                // прав после silent refresh. NavbarVertical слушает это событие и
                // пересчитывает access-фильтр под новый токен.
                if (typeof window !== 'undefined') {
                  window.dispatchEvent(new Event('auth-token-refreshed'));
                }
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                this.isRefreshing = false;
                this.processQueue(null, newToken);
                return this.axiosInstance(originalRequest);
              })
              .catch(err => {
                // ВАЖНО: сбросить флаг, иначе последующие 401 зависнут в очереди
                // навсегда (refresh уже провалился). handleRefreshTokenError уже
                // вызван внутри refreshToken() — будет один редирект на логин.
                this.isRefreshing = false;
                this.processQueue(err, null);
                return Promise.reject(err);
              });
          }

          return new Promise((resolve, reject) => {
            this.failedQueue.push({ resolve, reject });
          })
            .then(token => {
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              return this.axiosInstance(originalRequest);
            })
            .catch(err => {
              return Promise.reject(err);
            });
        }

        if (error.response) {
          const { status, data } = error.response;
          const errorMessages: { [key: number]: string } = {
            400: 'Некорректный запрос',
            401: 'Ошибка авторизации',
            403: 'Доступ запрещен',
            404: 'Ресурс не найден',
            500: 'Внутренняя ошибка сервера'
          };

          const errorMessage = errorMessages[status] || 'Произошла ошибка';
          console.error(`${errorMessage}:`, data);
        } else {
          console.error('Ошибка без ответа сервера:', error.message);
        }

        return Promise.reject(error);
      }
    );
  }
  private handleRefreshTokenError() {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    // Защита от шторма редиректов: при множестве параллельных 401 не дёргать
    // переход повторно и не зацикливаться, если уже на странице логина.
    if (this.redirectingToLogin) return;
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    if (path.includes('/authentication/')) return;
    this.redirectingToLogin = true;
    window.location.href = '/authentication/simple/login'; // Redirect to login
  }

  // Refresh token method
  private refreshToken(refreshToken: string | null): Promise<string> {
    const requestId = uuidv4();
    return this.axiosInstance
      .post(
        '/auth/v1/refresh',
        { refresh_token: refreshToken, refreshToken },
        {
          headers: {
            requestId
          }
        }
      )
      .then(response => response.data.data.token)
      .catch(error => {
        console.error(
          'Failed to refresh token:',
          error.response?.data || error.message
        );
        this.handleRefreshTokenError();
        throw error;
      });
  }

  private processQueue(error: any, token: string | null = null) {
    this.failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    this.failedQueue = [];
  }

  // GET method
  public get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.axiosInstance.get(url, config).then(response => response.data);
  }

  // POST method
  public post<T>(
    url: string,
    data: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    return this.axiosInstance
      .post(url, data, config)
      .then(response => response.data);
  }

  // DELETE method (generic)
  public delete<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.axiosInstance
      .delete(url, {
        ...config,
        data
      })
      .then(response => response.data);
  }

  // Получение транзакций с requestId в заголовке
  public fetchTransactions<ITransactionResponse>(
    filters: any
  ): Promise<ITransactionResponse> {
    if (!filters.trxId) {
      delete filters.trxId;
    }

    return this.axiosInstance
      .get<ITransactionResponse>('/operations/v1/get/', {
        headers: {
          requestId: uuidv4()
        },
        params: filters
      })
      .then(response => response.data);
  }

  // Метод поиска аккаунтов
  public searchAccounts(params: {
    search?: string;
    currency?: string;
    dateFrom?: string;
    dateTo?: string;
    page: number;
    limit: number;
  }): Promise<any> {
    return this.axiosInstance
      .get('/account/v1/list/', {
        params: {
          search: params.search,
          currency: params.currency,
          dateFrom: params.dateFrom,
          dateTo: params.dateTo,
          page: params.page,
          limit: params.limit
        }
      })
      .then(response => response.data);
  }

  // Получение транзакций для конкретного аккаунта
  public getTransactions(
    accountId: string,
    params: { page: number; limit: number }
  ): Promise<any> {
    return this.axiosInstance
      .get(`/account/v1/${accountId}/transactions`, { params })
      .then(response => response.data);
  }

  // PATCH запрос для сброса пароля. email обязателен — бэкенд ищет профиль по
  // phone/email и отправляет новый пароль письмом. Без него — 400.
  public resetPassword(requestId: string, email: string): Promise<any> {
    return this.axiosInstance
      .patch(
        '/profile/v1/resetPassword',
        { email },
        {
          headers: {
            requestId // Только requestId, Authorization добавляется автоматически
          }
        }
      )
      .then(response => {
        console.log('Password reset success:', response.data);
        return response.data;
      })
      .catch(error => {
        if (error.response) {
          if (error.response.status === 400) {
            console.error('Invalid request:', error.response.data);
          } else if (error.response.status === 401) {
            console.error('Authorization error:', error.response.data);
          } else {
            console.error('An error occurred:', error.response.data);
          }
        }
        throw error;
      });
  }

  // Админ сбрасывает пароль оператору по id: генерится временный пароль,
  // отправляется оператору на email, выставляется флаг обязательной смены.
  public profileResetPasswordAdmin(id: string): Promise<any> {
    return this.axiosInstance
      .post('/profile/v1/reset-password-admin', { id })
      .then(r => r.data);
  }

  // Админ задаёт оператору временный пароль (вводит сам / генерирует на фронте)
  // с флагом обязательной смены при первом входе.
  public profileSetTempPassword(id: string, password: string): Promise<any> {
    return this.axiosInstance
      .post('/profile/v1/set-temp-password', { id, password })
      .then(r => r.data);
  }

  // Оператор меняет СВОЙ пароль (завершение force-change при первом входе).
  // Бэкенд снимает флаг must_change_password.
  public updateOwnPassword(password: string): Promise<any> {
    return this.axiosInstance
      .patch('/profile/v1/password', { password })
      .then(r => r.data);
  }

  getOperators<T>(requestId: string): Promise<T> {
    return this.axiosInstance
      .get('/profile/v1/list', {
        headers: {
          requestId,
          Authorization: `Bearer ${localStorage.getItem('token')}` // Add Authorization header
        }
      })
      .then(response => response.data);
  }

  // POST request to add operator
  addOperator<T>(operatorData: any, requestId: string): Promise<T> {
    // Ensure you send the full expected operator data in the request
    const payload = {
      data: {
        id: operatorData.id || uuidv4(), // You can generate a new UUID or handle it dynamically
        name: operatorData.name, // Make sure name is not empty
        phone: operatorData.phone, // уникален в БД — не дефолтить, иначе 23505 на втором операторе
        type: operatorData.profileType || '1', // Default type
        status: operatorData.status || 'active', // Example status, can be 'blocked' or 'active'
        email: operatorData.email, // Make sure email is valid
        roles: operatorData.roles || [], // Array of roles
        group: operatorData.group,
        data: [operatorData.group] // Any additional data
      }
    };

    return this.axiosInstance
      .post('/profile/v1/add', payload, {
        headers: {
          requestId,
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })
      .then(response => response.data);
  }

  // PATCH request to update operator
  editOperator<T>(operatorData: any, requestId: string): Promise<T> {
    console.log(operatorData);
    const payload = {
      data: {
        id: operatorData.id, // You can generate a new UUID or handle it dynamically
        name: operatorData.name, // Make sure name is not empty
        phone: operatorData.phone || '+71234567890', // Example phone, replace with actual data
        type: operatorData.profileType || '1', // Default type
        status: operatorData.enabled ? 'active' : 'blocked', // Example status, can be 'blocked' or 'active'
        email: operatorData.email, // Make sure email is valid
        roles: operatorData.roles || [], // Array of roles
        group: operatorData.group,
        data: [operatorData.group] // Any additional data
      }
    };

    return this.axiosInstance
      .patch('/profile/v1/edit', payload, {
        headers: {
          requestId,
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })
      .then(response => response.data);
  }

  // DELETE request to delete operator
  deleteOperator<T>(operatorId: string, requestId: string): Promise<T> {
    return this.axiosInstance
      .delete('/profile/v1/delete', {
        headers: {
          requestId,
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        data: {
          id: operatorId // Send operator ID in the body
        }
      })
      .then(response => response.data);
  }
  // GET list of roles
  public getListRoles(requestId: string): Promise<any> {
    return this.axiosInstance
      .get('/profile/v1/list_roles', {
        headers: { requestId }
      })
      .then(response => response.data);
  }

  // POST new role
  public addRole<T>(data: any, requestId: string): Promise<T> {
    return this.axiosInstance
      .post('/profile/v1/add_role', data, {
        headers: { requestId }
      })
      .then(response => response.data);
  }

  // PUT edit existing role
  public editRole<T>(data: any, requestId: string): Promise<T> {
    return this.axiosInstance
      .put('/profile/v1/edit_role', data, {
        headers: { requestId }
      })
      .then(response => response.data);
  }

  // DELETE role
  public deleteRole<T>(idRole: string, requestId: string): Promise<T> {
    return this.axiosInstance
      .delete('/profile/v1/delete_role', {
        data: { idRole },
        headers: { requestId }
      })
      .then(response => response.data);
  }

  public getListAccess(requestId: string): Promise<any> {
    return this.axiosInstance
      .get('/profile/v1/list_access', {
        headers: { requestId }
      })
      .then(response => response.data);
  }
  public getLimits<T>(
    params: {
      codes?: string;
      ids?: string;
      types?: string;
      objects?: string;
      page?: number;
      size?: number;
    },
    requestId: string
  ): Promise<T> {
    return this.axiosInstance
      .get('/support/v1/limits', {
        headers: {
          requestId,
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        params
      })
      .then(response => response.data)
      .catch(error => {
        console.error(
          'Ошибка при получении лимитов:',
          error.response?.data || error.message
        );
        throw error;
      });
  }

  public addLimit<T>(limitData: any, requestId: string): Promise<T> {
    return this.axiosInstance
      .post('/support/v1/limits', limitData, {
        headers: {
          requestId,
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })
      .then(response => response.data);
  }

  public updateLimit<T>(limitData: any, requestId: string): Promise<T> {
    return this.axiosInstance
      .put('/support/v1/limits', limitData, {
        headers: {
          requestId,
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })
      .then(response => response.data);
  }

  public deleteLimit<T>(code: string, requestId: string): Promise<T> {
    return this.axiosInstance
      .delete('/support/v1/limits', {
        headers: {
          requestId,
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        data: {
          code
        }
      })
      .then(response => response.data);
  }
  public blockUser(userId: string, comment: string): Promise<any> {
    return this.axiosInstance.put(
      '/user/v1/block',
      { userId, comment }, // передаем userId и comment
      {
        headers: {
          requestId: uuidv4(),
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    );
  }

  public unblockUser(userId: string, comment: string): Promise<any> {
    return this.axiosInstance.put(
      '/user/v1/unblock',
      { userId, comment }, // передаем userId и comment
      {
        headers: {
          requestId: uuidv4(),
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    );
  }
  public getTemplates(): Promise<any> {
    return this.axiosInstance
      .get('/notify/v1/template', {
        headers: {
          requestId: uuidv4()
        }
      })
      .then(response => response.data)
      .catch(error => {
        console.error('Ошибка при получении шаблонов:', error);
        throw error;
      });
  }

  // Method to add a template
  public addTemplate(templateData: any): Promise<any> {
    return this.axiosInstance
      .post('/notify/v1/template', templateData, {
        headers: {
          requestId: uuidv4()
        }
      })
      .then(response => response.data)
      .catch(error => {
        console.error('Ошибка при добавлении шаблона:', error);
        throw error;
      });
  }

  // Method to update a template
  public updateTemplate(templateData: any): Promise<any> {
    return this.axiosInstance
      .put('/notify/v1/template', templateData, {
        headers: {
          requestId: uuidv4()
        }
      })
      .then(response => response.data)
      .catch(error => {
        console.error('Ошибка при обновлении шаблона:', error);
        throw error;
      });
  }

  // Method to delete a template
  public deleteTemplate(templateId: string): Promise<any> {
    return this.axiosInstance
      .delete('/notify/v1/template', {
        headers: {
          requestId: uuidv4(),
          templateId
        }
      })
      .then(response => response.data)
      .catch(error => {
        console.error('Ошибка при удалении шаблона:', error);
        throw error;
      });
  }
  // --- Sender (email/sms через Kafka → sender-worker → UniOne/BlackBox) ---

  // Отправка email через sender service
  public senderSendEmail(templateId: string, target: string, fromAddr: string, params: Record<string, string>, userId?: string): Promise<any> {
    return this.axiosInstance
      .post('/sender/v1/send-email', {
        template_id: templateId,
        target,
        from_addr: fromAddr,
        params,
        user_id: userId || '',
      }, {
        headers: { requestId: uuidv4() }
      })
      .then(response => response.data)
      .catch(error => {
        console.error('Ошибка при отправке email:', error);
        throw error;
      });
  }

  // Отправка SMS через sender service (BlackBox)
  public senderSendSms(templateId: string, target: string, params: Record<string, string>, userId?: string): Promise<any> {
    return this.axiosInstance
      .post('/sender/v1/send-sms', {
        template_id: templateId,
        target,
        params,
        user_id: userId || '',
      }, {
        headers: { requestId: uuidv4() }
      })
      .then(response => response.data)
      .catch(error => {
        console.error('Ошибка при отправке SMS:', error);
        throw error;
      });
  }

  // FCM push через sender pipeline (admin-api v60.9 — POST /sender/v1/send-push).
  // target в Kafka остаётся пустым: sender-worker сам резолвит активные
  // FCM-токены user_id через DeviceTokenResolver → users.GetDeviceTokens
  // (Phase 1.2 multicast). Template должен быть type=2 (push).
  public senderSendPush(templateId: string, userId: string, params: Record<string, string>): Promise<any> {
    return this.axiosInstance
      .post('/sender/v1/send-push', {
        template_id: templateId,
        user_id: userId,
        params,
      }, {
        headers: { requestId: uuidv4() }
      })
      .then(response => response.data)
      .catch(error => {
        console.error('Ошибка при отправке push:', error);
        throw error;
      });
  }

  // --- Notification Channels & Senders ---

  public getChannels(): Promise<any> {
    return this.axiosInstance.get('/notify/v1/channels').then(r => r.data);
  }
  public createChannel(data: any): Promise<any> {
    return this.axiosInstance.post('/notify/v1/channels', data).then(r => r.data);
  }
  public updateChannel(id: string, data: any): Promise<any> {
    return this.axiosInstance.put(`/notify/v1/channels/${id}`, data).then(r => r.data);
  }
  public deleteChannel(id: string): Promise<any> {
    return this.axiosInstance.delete(`/notify/v1/channels/${id}`).then(r => r.data);
  }

  public getNotifyParams(): Promise<any> {
    return this.axiosInstance.get('/notify/v1/params').then(r => r.data);
  }

  public getSenders(): Promise<any> {
    return this.axiosInstance.get('/notify/v1/senders').then(r => r.data);
  }
  public createSender(data: any): Promise<any> {
    return this.axiosInstance.post('/notify/v1/senders', data).then(r => r.data);
  }
  public updateSender(id: string, data: any): Promise<any> {
    return this.axiosInstance.put(`/notify/v1/senders/${id}`, data).then(r => r.data);
  }
  public deleteSender(id: string): Promise<any> {
    return this.axiosInstance.delete(`/notify/v1/senders/${id}`).then(r => r.data);
  }

  // --- Курсы (exchange/v1): провайдеры, пары, кросс-пары ---
  // GetProviders — список провайдеров
  public getProviders<T>(params: any): Promise<T> {
    return this.axiosInstance
        .get('/exchange/v1/provider', {
              headers: {
                      requestId: uuidv4(),
                      Authorization: `Bearer ${localStorage.getItem('token')}`
                    },
                params
            })
        .then(res => res.data)
        .catch(error => {
                console.error(
                  'Ошибка при получении провайдеров:',
                  error.response?.data || error.message
                );
                throw error;
              });
  }
  // CreateProvider — создание провайдера
  public addProvider<T>(addForm: { name: string; service: string }): Promise<T> {
      const payload = {
        name: addForm.name,
        service: addForm.service,
      };

  return this.axiosInstance
    .post('/exchange/v1/provider', payload, {
      headers: {
        requestId: uuidv4(),
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    })
    .then(response => response.data)
    .catch(error => {
      console.error('Ошибка при добавлении провайдера:', error);
      throw error;
    });
}
  // UpdateProvider — обновление провайдера
  public updateProvider<T>(payload: { id: number; name: string; service: string }): Promise<T> {
  return this.axiosInstance
    .put(`/exchange/v1/provider`, payload, {
      headers: {
        requestId: uuidv4(),
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      }
    })
    .then((response) => response.data)
    .catch((error) => {
      console.error('Ошибка при обновлении провайдера:', error);
      throw error;
    });
}
  // DeleteProvider — удаление провайдера
  public deleteProvider<T>(id: number): Promise<T> {
    return this.axiosInstance
      .delete(`/exchange/v1/provider?id=${id}`, {
        headers: {
          requestId: uuidv4(),
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        }
      })
      .then((response) => response.data)
      .catch((error) => {
        console.error('Ошибка при удалении провайдера:', error);
        throw error;
      });
  }

  // GetPairs — пары по providerId
  public getRates<T>(provider_id: number): Promise<T> {
   return this.axiosInstance
     .get(`/exchange/v1/pairs?provider_id=${provider_id}`, {
       headers: {
         requestId: uuidv4(),
         Authorization: `Bearer ${localStorage.getItem('token')}`,
         }
       })
     .then(res => res.data)
     .catch(error => {
             console.error(
               'Ошибка при получении торговых пар:',
               error.response?.data || error.message
             );
             throw error;
           });
}
  // UpdatePairs / EnableHistory — обновление пар, вкл/выкл истории и интервал обновления
  public updateRate<T>(payload: { pair_id: number; enable_history: boolean; update_interval: string }): Promise<T> {
    return this.axiosInstance
      .put(`/exchange/v1/pairs`, payload, {
        headers: {
          requestId: uuidv4(),
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        }
      })
      .then((response) => response.data)
      .catch((error) => {
        console.error('Ошибка при обновлении пары (UpdatePairs/EnableHistory):', error);
        throw error;
      });
  }

  public addRate<T>(payload: { id: number }): Promise<T> {
    return this.axiosInstance
      .post('/exchange/v1/pairs', payload.id, {
      headers: {
        requestId: uuidv4(),
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      }
    })
    .then(response => response.data)
    .catch(error => {
      console.error('Ошибка при добавлении пары:', error);
      throw error;
    });
  }

  // Курсы (custom). GET /exchange/v1/rates — параметры: provider_id, symbol, from, to
  public listRates<T = any[]>(params?: { provider_id?: number; symbol?: string; from?: string; to?: string }): Promise<T> {
    return this.axiosInstance
      .get('/exchange/v1/rates', {
        headers: {
          requestId: uuidv4(),
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        params: params || {}
      })
      .then(res => {
        const d = res.data;
        if (Array.isArray(d)) return d as T;
        if (d && typeof d === 'object' && Array.isArray(d.data)) return d.data as T;
        if (d?.code === 'ERCD0000' && Array.isArray(d.data)) return d.data as T;
        return (Array.isArray(d?.data) ? d.data : d?.data ?? []) as T;
      })
      .catch(error => {
        console.warn('listRates:', error.response?.data || error.message);
        throw error;
      });
  }

  // GetAvailableSymbols — все символы. GetProviderAvailableSymbols — символы по провайдеру. GET /exchange/v1/rates/symbols?provider_id=
  public getAvailableSymbols<T = string[]>(params?: { provider_id?: number }): Promise<T> {
    return this.axiosInstance
      .get('/exchange/v1/rates/symbols', {
        headers: {
          requestId: uuidv4(),
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        params: params || {}
      })
      .then(res => {
        const d = res.data;
        if (Array.isArray(d)) return d as T;
        if (d && typeof d === 'object' && Array.isArray(d.data)) return d.data as T;
        return (Array.isArray(d?.data) ? d.data : d?.data ?? []) as T;
      })
      .catch(error => {
        console.warn('getAvailableSymbols:', error.response?.data || error.message);
        throw error;
      });
  }

  // --- KYC /kyc/v1 ---
  // GET /kyc/v1/listUser — список пользователей
  public kycListUser<T = any>(): Promise<T> {
    return this.axiosInstance
      .get('/kyc/v1/listUser', {
        headers: {
          requestId: uuidv4(),
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })
      .then(res => {
        const d = res.data;
        if (Array.isArray(d)) return d as T;
        if (d && typeof d === 'object' && Array.isArray(d.data)) return d.data as T;
        return (d?.data ?? d) as T;
      })
      .catch(error => {
        console.warn('kycListUser:', error.response?.data || error.message);
        throw error;
      });
  }

  // GET /kyc/v1/getScore — скор (параметр: user_id или id пользователя)
  public kycGetScore<T = any>(params: { user_id?: string; [key: string]: any }): Promise<T> {
    return this.axiosInstance
      .get('/kyc/v1/getScore', {
        headers: {
          requestId: uuidv4(),
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        params,
      })
      .then(res => {
        const d = res.data;
        if (d && typeof d === 'object' && d.data !== undefined) return d.data as T;
        return d as T;
      })
      .catch(error => {
        console.warn('kycGetScore:', error.response?.data || error.message);
        throw error;
      });
  }

  // POST /kyc/v1/confirmUser — подтверждение пользователя
  public kycConfirmUser<T = any>(payload: { user_id?: string; [key: string]: any }): Promise<T> {
    return this.axiosInstance
      .post('/kyc/v1/confirmUser', payload ?? {}, {
        headers: {
          requestId: uuidv4(),
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })
      .then(res => res.data)
      .catch(error => {
        console.warn('kycConfirmUser:', error.response?.data || error.message);
        throw error;
      });
  }

  // POST /kyc/v1/rejectUser — отклонение пользователя
  public kycRejectUser<T = any>(payload: { user_id?: string; [key: string]: any }): Promise<T> {
    return this.axiosInstance
      .post('/kyc/v1/rejectUser', payload ?? {}, {
        headers: {
          requestId: uuidv4(),
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })
      .then(res => res.data)
      .catch(error => {
        console.warn('kycRejectUser:', error.response?.data || error.message);
        throw error;
      });
  }

  // POST /kyc/v1/refreshApplicant — заставляет KYC перезапросить данные у
  // SumSub и переписать локальный applicant + опубликовать Kafka, чтобы
  // users.users / user_addresses тоже подтянули свежее.
  public kycRefreshApplicant<T = any>(userId: string): Promise<T> {
    return this.axiosInstance
      .post('/kyc/v1/refreshApplicant', { user_id: userId }, {
        headers: {
          requestId: uuidv4(),
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })
      .then(res => res.data)
      .catch(error => {
        console.warn('kycRefreshApplicant:', error.response?.data || error.message);
        throw error;
      });
  }

  // GET /kyc/v1/questionnaire — данные SumSub-опросника пользователя
  public kycGetQuestionnaire<T = any>(userId: string): Promise<T> {
    return this.axiosInstance
      .get('/kyc/v1/questionnaire', {
        params: { user_id: userId },
        headers: {
          requestId: uuidv4(),
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })
      .then(res => res.data)
      .catch(error => {
        console.warn('kycGetQuestionnaire:', error.response?.data || error.message);
        throw error;
      });
  }

  // --- Поддержка /support/v1 ---
  private supportHeaders() {
    return {
      requestId: uuidv4(),
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    };
  }

  // GET /aml/v1/notification-configs — список конфигураций оповещений
  public supportListAlerts<T = any>(params?: Record<string, any>): Promise<T> {
    return this.axiosInstance
      .get('/aml/v1/notification-configs', { headers: this.supportHeaders(), params: params ?? {} })
      .then(res => {
        const d = res.data;
        if (Array.isArray(d)) return d as T;
        if (d && typeof d === 'object' && Array.isArray(d.data)) return d.data as T;
        return (d?.data ?? d) as T;
      })
      .catch(error => {
        console.warn('supportListAlerts:', error.response?.data || error.message);
        throw error;
      });
  }

  // POST /aml/v1/notification-configs — добавить конфигурацию оповещения
  public supportAddAlerts<T = any>(payload: any): Promise<T> {
    return this.axiosInstance
      .post('/aml/v1/notification-configs', payload ?? {}, { headers: this.supportHeaders() })
      .then(res => res.data)
      .catch(error => {
        console.warn('supportAddAlerts:', error.response?.data || error.message);
        throw error;
      });
  }

  // PUT /aml/v1/notification-configs/:id — редактировать конфигурацию оповещения
  public supportEditAlerts<T = any>(payload: any): Promise<T> {
    const id = payload?.id;
    const url = id ? `/aml/v1/notification-configs/${id}` : '/aml/v1/notification-configs';
    return this.axiosInstance
      .put(url, payload ?? {}, { headers: this.supportHeaders() })
      .then(res => res.data)
      .catch(error => {
        console.warn('supportEditAlerts:', error.response?.data || error.message);
        throw error;
      });
  }

  // DELETE /aml/v1/notification-configs/:id — удалить конфигурацию оповещения
  public supportDeleteAlerts<T = any>(params?: { id?: string; [key: string]: any }): Promise<T> {
    const id = params?.id;
    const url = id ? `/aml/v1/notification-configs/${id}` : '/aml/v1/notification-configs';
    return this.axiosInstance
      .delete(url, { headers: this.supportHeaders() })
      .then(res => res.data)
      .catch(error => {
        console.warn('supportDeleteAlerts:', error.response?.data || error.message);
        throw error;
      });
  }

  // GET /support/v1/getLogs — логи (501 — функционал в разработке, возвращаем [])
  public supportGetLogs<T = any>(_params?: Record<string, any>): Promise<T> {
    return Promise.resolve([] as unknown as T);
  }

  // v49 fix-6: GET /aml/v1/events — глобальный лог событий по AML-алертам.
  // Для вкладки «Логи» в Финмониторинге. Возвращает события aml_alert_events
  // с join'ом case_id/user_name/user_id.
  public amlListEvents<T = any>(params?: { limit?: number; offset?: number }): Promise<T> {
    return this.axiosInstance
      .get('/aml/v1/events', { params: { limit: params?.limit ?? 100, offset: params?.offset ?? 0 } })
      .then(res => (res.data?.data ?? []) as T)
      .catch(err => {
        console.warn('amlListEvents:', err?.response?.data || err?.message);
        return [] as unknown as T;
      });
  }

  // POST /support/v1/config — конфиг
  public supportConfig<T = any>(payload: any): Promise<T> {
    return this.axiosInstance
      .post('/support/v1/config', payload ?? {}, { headers: this.supportHeaders() })
      .then(res => res.data)
      .catch(error => {
        console.warn('supportConfig:', error.response?.data || error.message);
        throw error;
      });
  }

  // POST /support/v1/connect — подключение
  public supportConnect<T = any>(payload: any): Promise<T> {
    return this.axiosInstance
      .post('/support/v1/connect', payload ?? {}, { headers: this.supportHeaders() })
      .then(res => res.data)
      .catch(error => {
        console.warn('supportConnect:', error.response?.data || error.message);
        throw error;
      });
  }

  // POST /support/v1/featureToggles — фичи
  public supportFeatureToggles<T = any>(payload: any): Promise<T> {
    return this.axiosInstance
      .post('/support/v1/featureToggles', payload ?? {}, { headers: this.supportHeaders() })
      .then(res => res.data)
      .catch(error => {
        console.warn('supportFeatureToggles:', error.response?.data || error.message);
        throw error;
      });
  }

  // Проекты. Ответы бэкенда: { "code": "ok", "data": ... } (один объект или массив)
  // GET /project/v1 — список (ListProjectsHTTP)
  public getProjects<T = any[]>(): Promise<T> {
    return this.axiosInstance
      .get('/project/v1', {
        headers: {
          requestId: uuidv4(),
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })
      .then(res => {
        const d = res.data;
        if (d && d.code === 'ok' && Array.isArray(d.data)) return d.data;
        if (Array.isArray(d)) return d;
        return (d?.data ?? []) as T;
      })
      .catch(error => {
        console.warn('getProjects:', error.response?.data || error.message);
        return [] as T;
      });
  }

  // GET /project/v1/my — список активных проектов БЕЗ AccessProjectList.
  // Используется ProjectContext (navbar/switcher/per-project контексты).
  // Старый getProjects() остаётся для страницы «Проекты» (полный CRUD).
  // #4: сохранить выбранный проект «по умолчанию» (попадёт в JWT claim pid при
  // следующем login/refresh). Best-effort — ошибку проглатываем (не критично для UI).
  public async setDefaultProject(projectId: string | null): Promise<void> {
    try {
      await this.axiosInstance.post('/profile/v1/default-project', { project_id: projectId || '' });
    } catch (error: any) {
      console.warn('setDefaultProject:', error?.response?.data || error?.message);
    }
  }

  public getMyProjects<T = any[]>(): Promise<T> {
    return this.axiosInstance
      .get('/project/v1/my', {
        headers: {
          requestId: uuidv4(),
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })
      .then(res => {
        const d = res.data;
        if (d && d.code === 'ok' && Array.isArray(d.data)) return d.data;
        if (Array.isArray(d)) return d;
        return (d?.data ?? []) as T;
      })
      .catch(error => {
        console.warn('getMyProjects:', error.response?.data || error.message);
        return [] as T;
      });
  }

  // GET /project/v1/:id — проект по id (GetProjectHTTP)
  public getProject<T = any>(id: string): Promise<T> {
    return this.axiosInstance
      .get(`/project/v1/${id}`, {
        headers: {
          requestId: uuidv4(),
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })
      .then(res => {
        const d = res.data;
        if (d && d.code === 'ok' && d.data != null) return d.data;
        return d;
      })
      .catch(error => {
        console.error('getProject:', error);
        throw error;
      });
  }

  // POST /project/v1 — создание (AddProjectHTTP). Тело: name, project_data?, role?, is_active, id? (UUID)
  public createProject<T = any>(payload: {
    name: string;
    project_data?: Record<string, unknown>;
    role?: unknown[];
    is_active?: boolean;
    id?: string;
  }): Promise<T> {
    return this.axiosInstance
      .post('/project/v1', payload, {
        headers: {
          requestId: uuidv4(),
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })
      .then(res => {
        const d = res.data;
        if (d && d.code === 'ok' && d.data != null) return d.data;
        return d;
      })
      .catch(error => {
        console.error('createProject:', error);
        throw error;
      });
  }

  // PUT /project/v1/:id — обновление (UpdateProjectHTTP). Тело: name?, project_data?, role?, is_active?
  public updateProject<T = any>(id: string, payload: {
    name?: string;
    project_data?: Record<string, unknown>;
    role?: unknown[];
    is_active?: boolean;
  }): Promise<T> {
    return this.axiosInstance
      .put(`/project/v1/${id}`, payload, {
        headers: {
          requestId: uuidv4(),
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })
      .then(res => {
        const d = res.data;
        if (d && d.code === 'ok' && d.data != null) return d.data;
        return d;
      })
      .catch(error => {
        console.error('updateProject:', error);
        throw error;
      });
  }

  // GetCrossPairs — список кросс-пар
  public getCrossPairs<T>(): Promise<T> {
    return this.axiosInstance
      .get(`/exchange/v1/cross_pairs`, {
       headers: {
         requestId: uuidv4(),
         Authorization: `Bearer ${localStorage.getItem('token')}`,
         }
       })
     .then(res => res.data)
     .catch(error => {
             console.error(
               'Ошибка при получении кросс пар:',
               error.response?.data || error.message
             );
             throw error;
           });
  }

  // CreateCrossPair — создание кросс-пары
  public addCrossPairs<T>(payload: {
    symbol: string;
    discrepancy_limit: number;
    deviation_limit: number;
    active: boolean;
    reliable_source: { pair_id: number; type: string; status: string };
    corrective_rates: { pair_id: number; type: string; status: string }[];
    stabilizer_rates: { pair_id: number; type: string; status: string }[];
  }): Promise<T> {
    return this.axiosInstance
      .post('/exchange/v1/cross_pairs', payload, {
        headers: {
          requestId: uuidv4(),
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        }
      })
      .then(response => response.data)
      .catch(error => {
        console.error('Ошибка при создании кросс-пары:', error);
        throw error;
      });
  }

  // UpdateCrossPair — обновление кросс-пары
  public putCrossPairs<T>(payload: {
    id: number;
    symbol: string;
    discrepancy_limit: number;
    deviation_limit: number;
    active: boolean;
    reliable_source: { cross_id: number; pair_id: number; type: string; status: string };
    corrective_rates: { cross_id: number; pair_id: number; type: string; status: string }[];
    stabilizer_rates: { cross_id: number; pair_id: number; type: string; status: string }[];
  }): Promise<T> {
    return this.axiosInstance
      .put('/exchange/v1/cross_pairs', payload, {
        headers: {
          requestId: uuidv4(),
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        }
      })
      .then(response => response.data)
      .catch(error => {
        console.error('Ошибка при обновлении кросс-пары:', error);
        throw error;
      });
  }

  // DeleteCrossPair — удаление кросс-пары
  public deleteCrossPair<T>(id: number): Promise<T> {
    return this.axiosInstance
      .delete(`/exchange/v1/cross_pairs?id=${id}`, {
        headers: {
          requestId: uuidv4(),
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        }
      })
      .then(response => response.data)
      .catch(error => {
        console.error('Ошибка при удалении кросс-пары:', error);
        throw error;
      });
  }

  // --- ServiceConnect: базовый путь /service-connect/v1 ---

  private unwrapServiceConnectResponse<T>(res: { data?: unknown }): T {
    const d = res.data;
    if (d && typeof d === 'object' && (d as { code?: string }).code === 'ok' && (d as { data?: T }).data !== undefined)
      return (d as { data: T }).data;
    return d as T;
  }

  /** Список активных — GET /service-connect/v1 (ListServiceConnectsHTTP) */
  public listServiceConnects<T = any[]>(): Promise<T> {
    return this.axiosInstance
      .get('/service-connect/v1', {
        headers: { requestId: uuidv4(), Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      .then(res => {
        const d = res.data;
        if (d && d.code === 'ok' && Array.isArray(d.data)) return d.data;
        return Array.isArray(d) ? d : (d?.data ?? []);
      })
      .catch(error => {
        console.warn('listServiceConnects:', error.response?.data || error.message);
        throw error;
      });
  }

  /** Активный конфиг по project_id — GET /service-connect/v1/project/:projectId (GetServiceConnectByProjectIDHTTP). При 404 — null. */
  public getServiceConnectByProjectId<T = any>(projectId: string): Promise<T | null> {
    return this.axiosInstance
      .get(`/service-connect/v1/project/${encodeURIComponent(projectId)}`, {
        headers: { requestId: uuidv4(), Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      .then(res => {
        const d = res.data;
        if (d && d.code === 'ok' && d.data != null) return d.data;
        // Ответ вида { data: fullRecord } — возвращаем саму запись (fullRecord), чтобы у неё был .data = конфиг
        if (d && typeof d === 'object' && d.data != null && typeof d.data === 'object' && ('project_id' in d.data || 'id' in d.data))
          return d.data;
        return d != null ? d : null;
      })
      .catch(error => {
        if (error.response?.status === 404) return null;
        console.warn('getServiceConnectByProjectId:', error.response?.data || error.message);
        throw error;
      });
  }

  /**
   * OSS-NETVIZ-3: раскладка Карты сервисов текущего проекта (X-Project-ID авто).
   * GET /service-map/v1/layout → { positions, custom_services } или null.
   */
  public getServiceMapLayout(): Promise<{ positions?: any; custom_services?: any } | null> {
    return this.axiosInstance
      .get('/service-map/v1/layout')
      .then(res => {
        const d = res.data;
        return d && d.data != null ? d.data : null;
      })
      .catch(error => {
        console.warn('getServiceMapLayout:', error?.response?.data || error?.message);
        return null;
      });
  }

  /** OSS-NETVIZ-3: сохранить раскладку Карты сервисов текущего проекта. PUT /service-map/v1/layout */
  public saveServiceMapLayout(positions: any, customServices: any): Promise<any> {
    return this.axiosInstance
      .put('/service-map/v1/layout', { positions, custom_services: customServices })
      .then(res => res.data);
  }

  /**
   * Документация: манифест дерева доков текущего проекта (прокси к wiki через admin-api).
   * GET /docs/v1/manifest → { data: manifest | null, message? }.
   * data === null означает «доки не сконфигурированы/не опубликованы» (message поясняет).
   */
  public docsGetManifest(): Promise<{ manifest: any | null; message?: string }> {
    return this.axiosInstance
      .get('/docs/v1/manifest')
      .then(res => {
        const d = res.data;
        return { manifest: d && d.data != null ? d.data : null, message: d?.message };
      })
      .catch(error => {
        const msg = error?.response?.data?.message || error?.response?.data?.error || error?.message;
        console.warn('docsGetManifest:', msg);
        return { manifest: null, message: typeof msg === 'string' ? msg : 'Документация недоступна' };
      });
  }

  /**
   * Документация: тело документа (HTML) по пути из манифеста. GET /docs/v1/page?path=...
   * Возвращает HTML-строку (admin-api проксирует с wiki, путь ограничен projects/<slug>/).
   */
  public docsGetPage(path: string): Promise<string> {
    return this.axiosInstance
      .get('/docs/v1/page', { params: { path }, responseType: 'text', transformResponse: [(d: any) => d] })
      .then(res => (typeof res.data === 'string' ? res.data : ''));
  }

  /**
   * Задачи/бэклог из GitLab Work Items (common), фильтр по labels (CLAUDE.md §3.6).
   * GET /tasks/v1/issues?labels=&state= → { items, message? }. message при «не сконфигурировано».
   */
  public gitlabTasksGet(labels = 'backlog', state = 'opened'): Promise<{ items: any[]; message?: string }> {
    return this.axiosInstance
      .get('/tasks/v1/issues', { params: { labels, state } })
      .then(res => {
        const d = res.data;
        return { items: Array.isArray(d?.data) ? d.data : [], message: d?.message };
      })
      .catch(error => {
        const msg = error?.response?.data?.message || error?.response?.data?.error || error?.message;
        console.warn('gitlabTasksGet:', msg);
        return { items: [], message: typeof msg === 'string' ? msg : 'Не удалось загрузить задачи' };
      });
  }

  /** Управление GitLab-задачей (close/reopen/reassign/labels). PUT /tasks/v1/issues/:iid */
  public gitlabTaskUpdate(
    iid: number,
    body: { state_event?: string; assignee_id?: number; add_labels?: string; remove_labels?: string }
  ): Promise<any> {
    return this.axiosInstance.put(`/tasks/v1/issues/${iid}`, body).then(res => res.data);
  }

  /** Назначаемые участники проекта common (для reassign). GET /tasks/v1/members */
  public gitlabMembersGet(): Promise<Array<{ id: number; name: string; username: string }>> {
    return this.axiosInstance
      .get('/tasks/v1/members')
      .then(res => {
        const d = res.data;
        return Array.isArray(d?.data) ? d.data : [];
      })
      .catch(error => {
        console.warn('gitlabMembersGet:', error?.response?.data?.message || error?.message);
        return [];
      });
  }

  /** OSS-FLOW-2: кастомные рёбра Карты потоков данных текущего проекта. GET /diagrams/v1/service-graph */
  public getServiceGraphEdges(): Promise<any[]> {
    return this.axiosInstance
      .get('/diagrams/v1/service-graph')
      .then(res => {
        const d = res.data;
        return d && Array.isArray(d.data) ? d.data : [];
      })
      .catch(error => {
        console.warn('getServiceGraphEdges:', error?.response?.data || error?.message);
        return [];
      });
  }

  /** OSS-FLOW-2: добавить кастомное ребро. POST /diagrams/v1/service-graph/edges */
  public createServiceGraphEdge(edge: { source: string; target: string; type?: string; label?: string; is_async?: boolean }): Promise<any> {
    return this.axiosInstance
      .post('/diagrams/v1/service-graph/edges', edge)
      .then(res => (res.data && res.data.data) || null);
  }

  /** OSS-FLOW-2: удалить кастомное ребро. DELETE /diagrams/v1/service-graph/edges/:id */
  public deleteServiceGraphEdge(id: string): Promise<any> {
    return this.axiosInstance
      .delete(`/diagrams/v1/service-graph/edges/${encodeURIComponent(id)}`)
      .then(res => res.data);
  }

  /** Одна запись по id — GET /service-connect/v1/:id (GetServiceConnectHTTP) */
  public getServiceConnect<T = any>(id: string): Promise<T> {
    return this.axiosInstance
      .get(`/service-connect/v1/${id}`, {
        headers: { requestId: uuidv4(), Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      .then(res => this.unwrapServiceConnectResponse<T>(res))
      .catch(error => {
        console.error('getServiceConnect:', error);
        throw error;
      });
  }

  /** Создать — POST /service-connect/v1 (AddServiceConnectHTTP). Тело: project_id, project_name?, data, is_active? */
  public addServiceConnect<T = any>(payload: { project_id: string; project_name?: string; data?: unknown; is_active?: boolean }): Promise<T> {
    return this.axiosInstance
      .post('/service-connect/v1', payload, {
        headers: { requestId: uuidv4(), Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      .then(res => this.unwrapServiceConnectResponse<T>(res))
      .catch(error => {
        console.error('addServiceConnect:', error);
        throw error;
      });
  }

  /** Обновить — PUT /service-connect/v1/:id (UpdateServiceConnectHTTP) */
  public updateServiceConnect<T = any>(id: string, payload: { project_id?: string; project_name?: string; data?: unknown; is_active?: boolean }): Promise<T> {
    return this.axiosInstance
      .put(`/service-connect/v1/${id}`, payload, {
        headers: { requestId: uuidv4(), Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      .then(res => this.unwrapServiceConnectResponse<T>(res))
      .catch(error => {
        console.error('updateServiceConnect:', error);
        throw error;
      });
  }

  /** Мягкое удаление (is_active = false) — DELETE /service-connect/v1/:id (DeleteServiceConnectHTTP) */
  public deleteServiceConnect<T = any>(id: string): Promise<T> {
    return this.axiosInstance
      .delete(`/service-connect/v1/${id}`, {
        headers: { requestId: uuidv4(), Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      .then(res => (res.data && (res.data as any).data !== undefined ? (res.data as any).data : res.data))
      .catch(error => {
        console.error('deleteServiceConnect:', error);
        throw error;
      });
  }

  // ─── AML-методы ──────────────────────────────────────────────────────────────

  /**
   * Список AML-алертов с фильтрацией и пагинацией.
   */
  public async amlGetCases(filters: import('../components/aml/types').AmlFilter): Promise<import('../components/aml/types').AmlCasesResponse> {
    // v49 fix-4: ранее использовался default object с createdAt=undefined → "Invalid Date" в UI.
    // mapAmlTransaction объявлена снаружи класса (см. ниже amlGetCase) и обрабатывает JSONB
    // от scoring backend'а: {amount,currency,asset,scored_at,operation_type,scoring_request_id,tx_hash,from_address,to_address}.
    const params: Record<string, string> = {
      limit: String(filters.size),
      offset: String((filters.page - 1) * filters.size),
    };
    if (filters.status) params['status'] = filters.status;

    const response = await this.axiosInstance.get('/aml/v1/alerts', { params });
    const raw: any[] = response.data?.data ?? [];

    // Map backend AmlAlert shape to frontend AmlCase shape
    const { AmlCaseStatus, AmlRiskLevel } = await import('../components/aml/types');
    const { AML_RULES } = await import('../mocks/amlMocks');
    const data = raw.map((a: any) => ({
      id: a.id,
      caseId: a.case_id || '',
      userId: a.user_id ?? '',
      userEmail: a.user_email || '',
      userName: a.user_name || '',
      userPhone: a.user_phone ?? '',
      status: (a.status as any) ?? AmlCaseStatus.PENDING,
      riskLevel: (a.risk_level as any) ?? AmlRiskLevel.MEDIUM,
      riskScore: a.risk_score ?? 0,
      assignedTo: a.assigned_to,
      rulesCount: a.rules_count ?? 0,
      triggeredRules: (a.rules ?? []).map((r: any) => {
        const ruleInfo = AML_RULES.find((ar) => ar.code === r.rule_code);
        return {
          ruleId: r.id,
          ruleCode: r.rule_code,
          ruleName: ruleInfo?.name || r.rule_code,
          severity: r.severity ?? ruleInfo?.severity ?? 'medium',
          triggeredAt: r.triggered_at,
          details: ruleInfo?.description || r.rule_code,
          amount: r.amount,
          currency: r.currency,
        };
      }),
      transaction: mapAmlTransaction(a.transaction, a.created_at),
      scoringBreakdown: { total: a.risk_score ?? 0, kycScore: 0, transactionScore: 0, behaviorScore: 0, geoScore: 0, details: [] },
      timeline: (a.events ?? []).map((e: any) => ({
        id: e.id,
        eventType: e.event_type,
        description: e.description ?? '',
        occurredAt: e.created_at,
        actor: e.actor ?? 'system',
      })),
      decision: a.decision ? {
        id: a.decision.id,
        caseId: a.id,
        decision: a.decision.decision,
        reasonCode: a.decision.reason_code ?? '',
        comment: a.decision.comment ?? '',
        operatorId: a.decision.operator_id ?? '',
        operatorName: a.decision.operator_id ?? '',
        decidedAt: a.decision.created_at,
      } : undefined,
      createdAt: a.created_at,
      updatedAt: a.updated_at,
      dueAt: a.due_at || a.created_at,
    }));

    // Apply client-side search filter if provided (backend doesn't support free-text search yet)
    let filtered = data;
    if (filters.riskLevel) {
      filtered = filtered.filter((c) => c.riskLevel === filters.riskLevel);
    }
    if (filters.search) {
      const q = filters.search.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.id.toLowerCase().includes(q) ||
          c.userId.toLowerCase().includes(q) ||
          c.userEmail.toLowerCase().includes(q)
      );
    }

    return { data: filtered, total: filtered.length, page: filters.page, size: filters.size };
  }

  /**
   * Один AML-алерт по id.
   */
  public async amlGetCase(id: string): Promise<import('../components/aml/types').AmlCase> {
    const response = await this.axiosInstance.get(`/aml/v1/alerts/${id}`);
    const a = response.data?.data;
    if (!a) throw new Error(`AML case not found: ${id}`);
    const { AmlCaseStatus, AmlRiskLevel } = await import('../components/aml/types');
    const { AML_RULES } = await import('../mocks/amlMocks');
    return {
      id: a.id,
      caseId: a.case_id || '',
      userId: a.user_id ?? '',
      userEmail: a.user_email || '',
      userName: a.user_name || '',
      userPhone: a.user_phone ?? '',
      status: (a.status as any) ?? AmlCaseStatus.PENDING,
      riskLevel: (a.risk_level as any) ?? AmlRiskLevel.MEDIUM,
      riskScore: a.risk_score ?? 0,
      assignedTo: a.assigned_to,
      rulesCount: a.rules_count ?? (a.rules?.length ?? 0),
      triggeredRules: (a.rules ?? []).map((r: any) => {
        const ruleInfo = AML_RULES.find((ar) => ar.code === r.rule_code);
        return {
          ruleId: r.id,
          ruleCode: r.rule_code,
          ruleName: ruleInfo?.name || r.rule_code,
          severity: r.severity ?? ruleInfo?.severity ?? 'medium',
          triggeredAt: r.triggered_at,
          details: ruleInfo?.description || r.rule_code,
          amount: r.amount,
          currency: r.currency,
        };
      }),
      transaction: mapAmlTransaction(a.transaction, a.created_at),
      scoringBreakdown: { total: a.risk_score ?? 0, kycScore: 0, transactionScore: 0, behaviorScore: 0, geoScore: 0, details: [] },
      timeline: (a.events ?? []).map((e: any) => ({
        id: e.id,
        eventType: e.event_type,
        description: e.description ?? '',
        occurredAt: e.created_at,
        actor: e.actor ?? 'system',
      })),
      decision: a.decision ? {
        id: a.decision.id,
        caseId: a.id,
        decision: a.decision.decision,
        reasonCode: a.decision.reason_code ?? '',
        comment: a.decision.comment ?? '',
        operatorId: a.decision.operator_id ?? '',
        operatorName: a.decision.operator_id ?? '',
        decidedAt: a.decision.created_at,
      } : undefined,
      createdAt: a.created_at,
      updatedAt: a.updated_at,
      dueAt: a.due_at || a.created_at,
    };
  }

  /**
   * Назначить алерт оператору (no-op — нет отдельного эндпоинта).
   */
  public async amlAssignCase(id: string, operatorId: string): Promise<void> {
    console.info('[AML] amlAssignCase — no dedicated endpoint yet', id, operatorId);
  }

  /**
   * Принять решение по AML-алерту.
   */
  public async amlMakeDecision(
    id: string,
    decision: import('../components/aml/types').DecisionFormState
  ): Promise<void> {
    await this.axiosInstance.post(`/aml/v1/alerts/${id}/decision`, {
      decision: decision.decision,
      reason_code: decision.reasonCode,
      comment: decision.comment,
    });
  }

  /**
   * Статистика по AML-алертам — вычисляется из списка.
   */
  /**
   * Количество AML-алертов, требующих внимания оператора (pending + in_review).
   * Используется sidebar-badge'ом. Возвращает 0 при любой ошибке — бейдж
   * никогда не должен ломать рендер навбара.
   */
  public async amlGetActiveCount(): Promise<number> {
    try {
      const r = await this.axiosInstance.get('/aml/v1/alerts/count');
      return Number(r.data?.data?.count ?? 0) || 0;
    } catch {
      return 0;
    }
  }

  /**
   * История скоринга (v47): GET /aml/v1/scoring-history?page=&size=&user_q=&outcome=
   * Backend объединяет Marble + Crystal истории и подтягивает имена через users gRPC.
   * Параметры фильтра:
   *   - userQ: substring/exact match по UUID / email / phone / имени / tx_hash
   *   - outcome: '' | 'approve' | 'decline' | 'block_and_review' | 'review' | 'failed' | 'all'
   */
  public async scoringHistoryGet(params: {
    page?: number;
    size?: number;
    userQ?: string;
    outcome?: string;
    signal?: AbortSignal; // v48 SH-2: cancel при быстрой смене фильтров
  } = {}): Promise<import('../types/scoring').ScoringHistoryResponse> {
    const r = await this.axiosInstance.get('/aml/v1/scoring-history', {
      params: {
        page: params.page ?? 1,
        size: params.size ?? 50,
        user_q: params.userQ ?? '',
        outcome: params.outcome ?? '',
      },
      signal: params.signal,
    });
    const items: import('../types/scoring').ScoringHistoryItem[] = Array.isArray(r.data?.data) ? r.data.data : [];
    const page = r.data?.page ?? { page: params.page ?? 1, size: params.size ?? 50, total: items.length };
    return { items, total: page.total ?? 0, page: page.page ?? 1, size: page.size ?? 50 };
  }

  /**
   * Количество KYC-applicant'ов, ожидающих действий оператора (PENDING + ON_HOLD).
   * Те же gracefull-defaults что у amlGetActiveCount.
   */
  public async kycGetActiveCount(): Promise<number> {
    try {
      const r = await this.axiosInstance.get('/kyc/v1/count');
      return Number(r.data?.data?.count ?? 0) || 0;
    } catch {
      return 0;
    }
  }

  // --- Мониторинг (Phase 1, на существующих эндпоинтах) ---

  /** Инфра-здоровье: per-upstream gRPC state. Возвращает {upstreams[], allHealthy}. */
  public async healthUpstreams(): Promise<{ upstreams: any[]; allHealthy: boolean }> {
    const r = await this.axiosInstance.get('/health/upstreams');
    const d = r.data ?? {};
    const upstreams = Array.isArray(d.upstreams) ? d.upstreams
      : Array.isArray(d.data) ? d.data
      : Array.isArray(d) ? d : [];
    const allHealthy = d.all_healthy ?? d.allHealthy ?? upstreams.every((u: any) => u?.healthy);
    return { upstreams, allHealthy: !!allHealthy };
  }

  /** Сводка дашборда: operators/roles/active_sessions/actions_today/recent_activity. */
  public async dashboardStats(): Promise<any> {
    const r = await this.axiosInstance.get('/dashboard/v1/stats');
    return r.data?.data ?? r.data ?? {};
  }

  /** Доступность сервисов (Фаза 4): HTTP-код, latency, TLS до истечения. */
  public async monitoringServices(): Promise<{ services: any[]; allHealthy: boolean }> {
    const r = await this.axiosInstance.get('/monitoring/v1/services');
    const d = r.data ?? {};
    return { services: Array.isArray(d.data) ? d.data : [], allHealthy: !!d.healthy };
  }

  /** Базы данных (Фаза 5): Postgres-метрики (размер, коннекты, commits/sec, cache hit, deadlocks). */
  public async monitoringDatabases(): Promise<{ databases: any[]; allUp: boolean }> {
    const r = await this.axiosInstance.get('/monitoring/v1/databases');
    const d = r.data ?? {};
    return { databases: Array.isArray(d.data) ? d.data : [], allUp: !!d.healthy };
  }

  /** Synthetic E2E (Фаза 7): сводка сценариев (last run, latency, 24ч pass-rate). */
  public async monitoringE2E(): Promise<any[]> {
    const r = await this.axiosInstance.get('/monitoring/v1/e2e');
    return Array.isArray(r.data?.data) ? r.data.data : [];
  }

  /** Сброс пароля пользователя по user_id (запускает пользовательский reset-flow). */
  public async userResetPasswordById(userId: string): Promise<void> {
    await this.axiosInstance.post('/user/v1/reset-password', { user_id: userId });
  }

  /** Временный пароль пользователю (must_change=true — смена при первом входе). */
  public async userSetTempPassword(userId: string, password: string): Promise<void> {
    await this.axiosInstance.post('/user/v1/set-temp-password', { user_id: userId, password });
  }

  // --- Управление продуктом: бэклог (PM Phase 1, action-router) ---

  /**
   * Единая ручка бэклога POST /backlog/v1.
   * action ∈ {list,get,create,update,delete,comment,delete_comment}.
   * Чтение → backlogView, мутации → backlogEdit. Per-project через X-Project-ID.
   */
  public async backlogAction(action: string, payload: Record<string, any> = {}): Promise<any> {
    const r = await this.axiosInstance.post('/backlog/v1', { action, ...payload });
    return r.data?.data ?? null;
  }

  public async amlGetStats(): Promise<import('../components/aml/types').AmlStats> {
    const response = await this.axiosInstance.get('/aml/v1/alerts', { params: { limit: 1000, offset: 0 } });
    const raw: any[] = response.data?.data ?? [];
    const { AmlCaseStatus, AmlRiskLevel } = await import('../components/aml/types');
    return {
      total:              raw.length,
      pending:            raw.filter((c) => c.status === AmlCaseStatus.PENDING).length,
      inReview:           raw.filter((c) => c.status === AmlCaseStatus.IN_REVIEW).length,
      approved:           raw.filter((c) => c.status === AmlCaseStatus.APPROVED).length,
      rejected:           raw.filter((c) => c.status === AmlCaseStatus.REJECTED).length,
      escalated:          raw.filter((c) => c.status === AmlCaseStatus.ESCALATED).length,
      overdue:            raw.filter((c) => c.status === AmlCaseStatus.OVERDUE).length,
      criticalCount:      raw.filter((c) => c.risk_level === AmlRiskLevel.CRITICAL).length,
      avgResolutionHours: 4.5,
    };
  }

  // ─── AML Scenarios CRUD ───────────────────────────────────────────────────────

  /**
   * Список AML-сценариев.
   */
  public async amlGetScenarios(): Promise<any[]> {
    const response = await this.axiosInstance.get('/aml/v1/scenarios');
    return response.data?.data ?? [];
  }

  /**
   * Создать AML-сценарий.
   */
  public async amlCreateScenario(scenario: {
    code: string;
    name: string;
    description?: string;
    category?: string;
    threshold?: number;
    currency?: string;
    severity?: string;
    is_active?: boolean;
  }): Promise<any> {
    const response = await this.axiosInstance.post('/aml/v1/scenarios', scenario);
    return response.data?.data;
  }

  /**
   * Обновить AML-сценарий.
   */
  public async amlUpdateScenario(id: string, scenario: {
    code?: string;
    name?: string;
    description?: string;
    category?: string;
    threshold?: number;
    currency?: string;
    severity?: string;
    is_active?: boolean;
  }): Promise<any> {
    const response = await this.axiosInstance.put(`/aml/v1/scenarios/${id}`, scenario);
    return response.data?.data;
  }

  /**
   * Удалить AML-сценарий.
   */
  public async amlDeleteScenario(id: string): Promise<void> {
    await this.axiosInstance.delete(`/aml/v1/scenarios/${id}`);
  }

  /**
   * Список операций скоринга (для AML).
   */
  public async amlGetOperations(limit = 25, offset = 0): Promise<any[]> {
    const response = await this.axiosInstance.get('/aml/v1/operations', { params: { limit, offset } });
    return response.data?.data ?? [];
  }

  /**
   * История запросов к провайдерам Marble / Crystal по кейсу.
   */
  public async amlGetProviderHistory(
    caseId: string,
    source?: string,
    limit = 20,
    offset = 0,
  ): Promise<import('../components/aml/types').ProviderHistoryResponse> {
    const params: Record<string, string | number> = { limit, offset };
    if (source) params.source = source;
    const response = await this.axiosInstance.get(
      `/aml/v1/alerts/${caseId}/provider-history`,
      { params },
    );
    const data = response.data ?? {};
    const rawItems: any[] = data.items ?? [];
    return {
      items: rawItems.map((item: any) => ({
        id:          item.id ?? '',
        source:      item.source ?? '',
        occurredAt:  item.occurred_at ?? item.occurredAt ?? '',
        refId:       item.ref_id ?? item.refId,
        status:      item.status ?? 'error',
        summary:     item.summary ?? '',
        score:       item.score ?? 0,
        payload:     item.raw_response ? (() => { try { return JSON.parse(item.raw_response); } catch { return item.raw_response; } })() : null,
      })),
      total:  data.total  ?? 0,
      limit:  data.limit  ?? limit,
      offset: data.offset ?? offset,
    };
  }

  // ─── Diagrams (swimlane scenarios) ───

  /**
   * Получить все сценарии из БД.
   */
  public async diagramListScenarios(): Promise<any[]> {
    const response = await this.axiosInstance.get('/diagrams/v1/scenarios');
    return response.data?.data ?? [];
  }

  /**
   * Сохранить (создать/обновить) сценарий в БД.
   */
  public async diagramSaveScenario(scenario: any): Promise<any> {
    const response = await this.axiosInstance.put(`/diagrams/v1/scenarios/${scenario.id}`, scenario);
    return response.data?.data;
  }

  /**
   * Удалить сценарий из БД.
   */
  public async diagramDeleteScenario(id: string): Promise<void> {
    await this.axiosInstance.delete(`/diagrams/v1/scenarios/${id}`);
  }

  /**
   * Получить справочник фигур.
   */
  public async diagramGetShapes(): Promise<any[]> {
    const response = await this.axiosInstance.get('/diagrams/v1/shapes');
    return response.data?.data ?? [];
  }

  /**
   * Получить сохранённые позиции сценария.
   */
  public async diagramGetPositions(scenarioId: string): Promise<Record<string, { x: number; y: number }>> {
    const response = await this.axiosInstance.get(`/diagrams/v1/positions/${scenarioId}`);
    return response.data?.data ?? {};
  }

  /**
   * Сохранить позиции элементов сценария.
   */
  public async diagramSavePositions(scenarioId: string, positions: Record<string, { x: number; y: number }>): Promise<void> {
    await this.axiosInstance.put(`/diagrams/v1/positions/${scenarioId}`, { positions });
  }

  // --- DRAWIO-1: свободные схемы drawio (per-project через X-Project-ID interceptor) ---

  /** Список схем (без xml/svg — только метаданные). */
  public async drawioList(): Promise<Array<{ id: string; name: string; updated_at: string }>> {
    const response = await this.axiosInstance.get('/diagrams/v1/drawio');
    return response.data?.data ?? [];
  }

  /** Полная схема (xml+svg) по id. */
  public async drawioGet(id: string): Promise<{ id: string; name: string; xml: string; svg?: string }> {
    const response = await this.axiosInstance.get(`/diagrams/v1/drawio/${id}`);
    return response.data?.data;
  }

  /** Создать схему, вернуть id. */
  public async drawioCreate(payload: { name: string; xml?: string; svg?: string }): Promise<string> {
    const response = await this.axiosInstance.post('/diagrams/v1/drawio', payload);
    return response.data?.data?.id;
  }

  /** Обновить схему (name/xml/svg). */
  public async drawioUpdate(id: string, payload: { name: string; xml: string; svg?: string }): Promise<void> {
    await this.axiosInstance.put(`/diagrams/v1/drawio/${id}`, payload);
  }

  /** Удалить схему. */
  public async drawioDelete(id: string): Promise<void> {
    await this.axiosInstance.delete(`/diagrams/v1/drawio/${id}`);
  }

  /**
   * Импорт сценария из JSON.
   */
  public async diagramImportScenario(scenario: any): Promise<any> {
    const response = await this.axiosInstance.post('/diagrams/v1/import', scenario);
    return response.data?.data;
  }

  /**
   * Экспорт сценария в JSON.
   */
  public async diagramExportScenario(id: string): Promise<any> {
    const response = await this.axiosInstance.get(`/diagrams/v1/export/${id}`);
    return response.data?.data;
  }

  // OTP Channel Routing (RFC-003: Telegram Gateway)
  public getOtpRouting<T = any>(): Promise<T> {
    return this.axiosInstance
      .get('/notify/v1/otp-routing', {
        headers: { requestId: uuidv4(), Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      .then(res => res.data?.data ?? res.data ?? [])
      .catch(error => { console.warn('getOtpRouting:', error.message); return []; });
  }

  public createOtpRouting<T = any>(payload: { phone_prefix: string; priority_channel: string; fallback_channel: string; is_active: boolean }): Promise<T> {
    return this.axiosInstance
      .post('/notify/v1/otp-routing', payload, {
        headers: { requestId: uuidv4(), Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      .then(res => res.data?.data ?? res.data);
  }

  public updateOtpRouting<T = any>(id: string, payload: { phone_prefix: string; priority_channel: string; fallback_channel: string; is_active: boolean }): Promise<T> {
    return this.axiosInstance
      .put(`/notify/v1/otp-routing/${id}`, payload, {
        headers: { requestId: uuidv4(), Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      .then(res => res.data?.data ?? res.data);
  }

  public deleteOtpRouting(id: string): Promise<void> {
    return this.axiosInstance
      .delete(`/notify/v1/otp-routing/${id}`, {
        headers: { requestId: uuidv4(), Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      .then(() => undefined);
  }

  // Notification Event Rules — матрица event × channel
  public getEventRules<T = any>(): Promise<T> {
    return this.axiosInstance
      .get('/notify/v1/event-rules', {
        headers: { requestId: uuidv4(), Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      .then(res => res.data?.data ?? res.data ?? [])
      .catch(error => { console.warn('getEventRules:', error.message); return []; });
  }

  public upsertEventRules<T = any>(rules: Array<{ event_code: string; channel_code: string; enabled: boolean }>): Promise<T> {
    return this.axiosInstance
      .put('/notify/v1/event-rules', { rules }, {
        headers: { requestId: uuidv4(), Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      .then(res => res.data?.data ?? res.data);
  }

  public getEventCatalog<T = any>(): Promise<T> {
    return this.axiosInstance
      .get('/notify/v1/event-rules/events', {
        headers: { requestId: uuidv4(), Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      .then(res => res.data?.data ?? res.data ?? [])
      .catch(error => { console.warn('getEventCatalog:', error.message); return []; });
  }

  public getChannelCatalog<T = any>(): Promise<T> {
    return this.axiosInstance
      .get('/notify/v1/event-rules/channels', {
        headers: { requestId: uuidv4(), Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      .then(res => res.data?.data ?? res.data ?? [])
      .catch(error => { console.warn('getChannelCatalog:', error.message); return []; });
  }
};
export default new ApiService();

